import React, { useState } from 'react'
import { OrgStatementTinyMceField } from './OrgStatementTinyMceField'
import './OrgStatementReorder.css'

export const FormOrganizationDetails = ({
  data,
  setFormDatum,
  onSubmit,
  readonly,
  customColumns = false,
  colClass = 'col-12 col-md-6',
  groupedFields = [],
  statementEditorKeyPrefix,
  enableGroupReorder = false,
  onReorderGroups,
}: any) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  // @ts-ignore
  let formParsedValues = {
    "date": {
      "value": data.date,
      "type": "text",
      "name": "date",
      "human_name": "Date",
      "required": true,
      "invalid_feedback": "Please add a valid date"
    },
    "name": {
      "value": data.name,
      "type": "text",
      "name": "name",
      "human_name": "Name",
      "required": true,
      "invalid_feedback": "Please add the organization name"
    },
    "contact": {
      "value": data.contact,
      "type": "text",
      "name": "contact",
      "human_name": "Contact",
      "required": true,
      "invalid_feedback": "Please add the contact name"
    },
    "url": {
      "value": data.url,
      "type": "text",
      "name": "url",
      "human_name": "Web Address",
      "required": false,
      "invalid_feedback": null
    },
    "email": {
      "value": data.email,
      "type": "email",
      "name": "email",
      "human_name": "Email",
      "required": false,
      "invalid_feedback": null
    },
    "address": {
      "value": data.address,
      "type": "text",
      "name": "address",
      "human_name": "Address",
      "required": false,
      "invalid_feedback": null
    },
    "address2": {
      "value": data.address2,
      "type": "text",
      "name": "address2",
      "human_name": "Address 2",
      "required": false,
      "invalid_feedback": null
    },
    "city": {
      "value": data.city,
      "type": "text",
      "name": "city",
      "human_name": "City",
      "required": false,
      "invalid_feedback": null
    },
    "state": {
      "value": data.state,
      "type": "text",
      "name": "state",
      "human_name": "State",
      "required": false,
      "invalid_feedback": null
    },
    "zip": {
      "value": data.zip,
      "type": "text",
      "name": "zip",
      "human_name": "Zip",
      "required": false,
      "invalid_feedback": null
    },
    "country": {
      "value": data.country,
      "type": "text",
      "name": "country",
      "human_name": "Country",
      "required": false,
      "invalid_feedback": null
    },
    "phone": {
      "value": data.phone,
      "type": "tel",
      "name": "phone",
      "human_name": "Phone",
      "required": false,
      "invalid_feedback": null
    },
    "phone2": {
      "value": data.phone2,
      "type": "tel",
      "name": "phone2",
      "human_name": "Phone 2",
      "required": false,
      "invalid_feedback": null
    },
    "type": {
      "value": data.type,
      "type": "text",
      "name": "type",
      "human_name": "Type",
      "required": false,
      "invalid_feedback": null
    },
    "focus": {
      "value": data.focus,
      "type": "text",
      "name": "focus",
      "human_name": "Focus",
      "required": false,
      "invalid_feedback": null
    },
    "employees": {
      "value": data.employees,
      "type": "text",
      "name": "employees",
      "human_name": "Employees",
      "required": false,
      "invalid_feedback": null
    },
    "locations": {
      "value": data.locations,
      "type": "text",
      "name": "locations",
      "human_name": "Locations",
      "required": false,
      "invalid_feedback": null
    },
    "referred_by": {
      "value": data.referred_by,
      "type": "text",
      "name": "referred_by",
      "human_name": "Referred By",
      "required": false,
      "invalid_feedback": null
    },
    "status": {
      "value": data.status,
      "type": "dropdown",
      "name": "status",
      "human_name": "Status",
      "required": false,
      "invalid_feedback": null,
      "dropdownOptions": [
        {
          "value": "Active",
          "name": "Active"
        },
        {
          "value": "Inactive",
          "name": "Inactive"
        }
      ]
    },
    "notes": {
      "value": data.notes,
      "type": "textarea",
      "name": "notes",
      "human_name": "Notes",
      "required": false,
      "invalid_feedback": null
    }
  }

  if(customColumns != false) {
    formParsedValues = customColumns;
  }

  // Get fields that are part of groups
  const groupedFieldNames = new Set();
  groupedFields.forEach(group => {
    group.forEach(fieldName => groupedFieldNames.add(fieldName));
  });

  // Get fields that are NOT part of groups
  const standaloneFields = Object.keys(formParsedValues).filter(fieldName => !groupedFieldNames.has(fieldName));

  const isReorderableGroup = (group: string[]) => {
    if (!enableGroupReorder || typeof onReorderGroups !== 'function' || readonly) {
      return false;
    }
    const firstField = formParsedValues[group[0]];
    return firstField?.type === 'tinymce';
  };

  const renderCheckboxField = (field: any) => (
    <div className="form-check">
      <input
        type="checkbox"
        className="form-check-input"
        id={field.name}
        name={field.name}
        checked={field.value === 1 || field.value === true}
        onChange={(e) => {
          const event = {
            target: {
              name: e.target.name,
              value: e.target.checked ? 1 : 0
            }
          };
          setFormDatum(event);
        }}
        disabled={readonly}
      />
      <label className="form-check-label" htmlFor={field.name}>
        {field.checkboxLabel || 'Enable'}
      </label>
    </div>
  );

  // @ts-ignore
  return (
    <form id="dataFormDetails" onSubmit={onSubmit} noValidate>
      <div className="row">
        {/* Render standalone fields first */}
        {
          standaloneFields.map((row) => (
            <div className={colClass} key={row}>
              <div className="mb-3">
                {formParsedValues[row].human_name && (
                  <label
                    htmlFor="date"
                    className="form-label fw-bold"
                  >{formParsedValues[row].human_name}
                    {formParsedValues[row].required && <span style={{color: 'red'}}>*</span>}
                  </label>
                )}
                {
                  formParsedValues[row].type === 'dropdown' ?
                    (<select
                        className="form-select"
                        id={formParsedValues[row].name}
                        name={formParsedValues[row].name}
                        placeholder={formParsedValues[row].human_name}
                        autoComplete="off"
                        value={formParsedValues[row].value}
                        onChange={setFormDatum}
                        disabled={readonly}
                      >
                        {
                          formParsedValues[row].dropdownOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.name}</option>
                          ))
                        }
                      </select>
                    )
                    :
                    (
                      formParsedValues[row].type === 'tinymce' ?
                        (<OrgStatementTinyMceField
                            key={`${statementEditorKeyPrefix ?? 'org'}-${formParsedValues[row].name}`}
                            id={`org-stmt-${formParsedValues[row].name}`}
                            name={formParsedValues[row].name}
                            value={formParsedValues[row].value}
                            readonly={readonly}
                            onChange={(name, value) => setFormDatum({ target: { name, value } })}
                        />)
                    :
                      formParsedValues[row].type === 'textarea' ?
                        (<textarea
                            className="form-control"
                            style={{ width: '100%', backgroundColor: '#f8f9fa' }}
                            id={formParsedValues[row].name}
                            name={formParsedValues[row].name}
                            placeholder={formParsedValues[row].human_name}
                            autoComplete="off"
                            required={formParsedValues[row].required}
                            value={formParsedValues[row].value}
                            onChange={setFormDatum}
                            readOnly={readonly}
                            disabled={readonly}
                        />
                    )
                    :
                    (
                      formParsedValues[row].type === 'checkbox' ?
                        renderCheckboxField(formParsedValues[row])
                        :
                        (
                          <input
                            type={formParsedValues[row].type}
                            className="form-control"
                            style={{ backgroundColor: '#f8f9fa' }}
                            id={formParsedValues[row].name}
                            name={formParsedValues[row].name}
                            placeholder={formParsedValues[row].human_name}
                            autoComplete="off"
                            required={formParsedValues[row].required}
                            value={formParsedValues[row].value}
                            onChange={setFormDatum}
                            readOnly={readonly}
                            disabled={readonly}
                          />
                        )
                    )
                  )
                }
                {
                  formParsedValues[row].invalid_feedback &&
                  <div className="invalid-feedback">
                    {formParsedValues[row].invalid_feedback}
                  </div>
                }
              </div>
            </div>
          ))
        }

        {/* Render grouped fields */}
        {
          groupedFields.map((group, groupIndex) => {
            const reorderable = isReorderableGroup(group);
            const primaryField = formParsedValues[group[0]];
            const hideFieldName = group.find((fieldName: string) => formParsedValues[fieldName]?.type === 'checkbox');
            const hideField = hideFieldName ? formParsedValues[hideFieldName] : null;
            const editorFieldName = group.find((fieldName: string) => {
              const type = formParsedValues[fieldName]?.type;
              return type === 'tinymce' || type === 'textarea';
            }) || group[0];
            const editorField = formParsedValues[editorFieldName];

            return (
              <div className={colClass} key={group[0] || groupIndex}>
                <div
                  className={[
                    'mb-3',
                    reorderable ? 'org-stmt-group' : '',
                    reorderable && dragIndex === groupIndex ? 'is-dragging' : '',
                    reorderable && dragOverIndex === groupIndex && dragIndex !== groupIndex ? 'is-drag-over' : '',
                  ].filter(Boolean).join(' ')}
                  onDragOver={reorderable ? (event) => {
                    event.preventDefault();
                    if (dragOverIndex !== groupIndex) {
                      setDragOverIndex(groupIndex);
                    }
                  } : undefined}
                  onDrop={reorderable ? (event) => {
                    event.preventDefault();
                    if (dragIndex !== null && dragIndex !== groupIndex) {
                      onReorderGroups(dragIndex, groupIndex);
                    }
                    setDragIndex(null);
                    setDragOverIndex(null);
                  } : undefined}
                  onDragLeave={reorderable ? () => {
                    if (dragOverIndex === groupIndex) {
                      setDragOverIndex(null);
                    }
                  } : undefined}
                >
                  {reorderable ? (
                    <>
                      <div
                        className="org-stmt-group-header is-reorderable"
                        draggable
                        onDragStart={(event) => {
                          setDragIndex(groupIndex);
                          event.dataTransfer.effectAllowed = 'move';
                          try {
                            event.dataTransfer.setData('text/plain', String(groupIndex));
                          } catch {
                            // IE / older browsers
                          }
                        }}
                        onDragEnd={() => {
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                      >
                        <span className="org-stmt-grabber" aria-hidden="true" title="Drag to reorder">
                          <span />
                          <span />
                          <span />
                        </span>
                        {primaryField?.human_name && (
                          <span className="org-stmt-title">
                            {primaryField.human_name}
                            {primaryField.required && <span style={{color: 'red'}}>*</span>}
                          </span>
                        )}
                        {hideField && renderCheckboxField(hideField)}
                      </div>
                      {editorField?.type === 'tinymce' ? (
                        <OrgStatementTinyMceField
                          key={`${statementEditorKeyPrefix ?? 'org'}-${editorField.name}`}
                          id={`org-stmt-${editorField.name}`}
                          name={editorField.name}
                          value={editorField.value}
                          readonly={readonly}
                          onChange={(name, value) => setFormDatum({ target: { name, value } })}
                        />
                      ) : editorField?.type === 'textarea' ? (
                        <textarea
                          className="form-control"
                          style={{ width: '100%', backgroundColor: '#f8f9fa' }}
                          id={editorField.name}
                          name={editorField.name}
                          placeholder={editorField.human_name}
                          autoComplete="off"
                          required={editorField.required}
                          value={editorField.value}
                          onChange={setFormDatum}
                          readOnly={readonly}
                          disabled={readonly}
                        />
                      ) : null}
                      {editorField?.invalid_feedback && (
                        <div className="invalid-feedback">
                          {editorField.invalid_feedback}
                        </div>
                      )}
                    </>
                  ) : (
                    group.map((fieldName: string, fieldIndex: number) => {
                      const field = formParsedValues[fieldName];
                      if (!field) return null;
                      return (
                        <div key={fieldName}>
                          {fieldIndex === 0 && field.human_name && (
                            <label
                              htmlFor={field.name}
                              className="form-label fw-bold"
                            >{field.human_name}
                              {field.required && <span style={{color: 'red'}}>*</span>}
                            </label>
                          )}

                          {field.type === 'tinymce' ? (
                            <OrgStatementTinyMceField
                              key={`${statementEditorKeyPrefix ?? 'org'}-${field.name}`}
                              id={`org-stmt-${field.name}`}
                              name={field.name}
                              value={field.value}
                              readonly={readonly}
                              onChange={(name, value) => setFormDatum({ target: { name, value } })}
                            />
                          ) : field.type === 'textarea' ? (
                            <textarea
                              className="form-control"
                              style={{ width: '100%', backgroundColor: '#f8f9fa' }}
                              id={field.name}
                              name={field.name}
                              placeholder={field.human_name}
                              autoComplete="off"
                              required={field.required}
                              value={field.value}
                              onChange={setFormDatum}
                              readOnly={readonly}
                              disabled={readonly}
                            />
                          ) : field.type === 'checkbox' ? (
                            renderCheckboxField(field)
                          ) : (
                            <input
                              type={field.type}
                              className="form-control"
                              style={{ backgroundColor: '#f8f9fa' }}
                              id={field.name}
                              name={field.name}
                              placeholder={field.human_name}
                              autoComplete="off"
                              required={field.required}
                              value={field.value}
                              onChange={setFormDatum}
                              readOnly={readonly}
                              disabled={readonly}
                            />
                          )}

                          {field.invalid_feedback && (
                            <div className="invalid-feedback">
                              {field.invalid_feedback}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })
        }
      </div>
    </form>
  )
}
